import { Dayjs } from 'dayjs';
import { ARCHIVED_PROJECT_TOOLTIP } from '../../scripts/permission-utils';

export interface BidSetupRequiredFields {
  stateLocation: string;
  contactUserId: string;
  liveAtDate: Dayjs | null;
  bidDate: Dayjs | null;
  addendaDueDate: Dayjs | null;
  submissionDate: Dayjs | null;
  allowElectronicRfi: boolean;
  allowElectronicSubstitutionRequest: boolean;
}

/**
 * The bid setup form can only be published once every required field is filled in. The Project
 * Contact is one of them: the contact is the architect of record bidders correspond with, and the
 * private-project invitation email is generated from that user's company.
 */
export const isBidSetupIncomplete = (fields: BidSetupRequiredFields): boolean => {
  const {
    stateLocation,
    contactUserId,
    liveAtDate,
    bidDate,
    addendaDueDate,
    submissionDate,
    allowElectronicRfi,
    allowElectronicSubstitutionRequest,
  } = fields;
  return (
    !stateLocation ||
    !contactUserId ||
    !liveAtDate ||
    !bidDate ||
    !addendaDueDate ||
    ((allowElectronicRfi || allowElectronicSubstitutionRequest) && !submissionDate)
  );
};

/**
 * Sending a private project invitation (and the password it carries) needs the *published* contact
 * user of the bid setup — the backend rejects the send without one — so an existing bid setup whose
 * contact has never been published cannot invite bidders yet.
 */
export const isMissingPublishedProjectContact = (
  bidSetupId: string | null | undefined,
  publishedContactUserId: string | null | undefined,
): boolean => !!bidSetupId && !publishedContactUserId;

export const MISSING_PROJECT_CONTACT_TOOLTIP =
  'Set a Project Contact and publish your changes before inviting bidders to this project.';

export const PUBLIC_PROJECT_TOOLTIP = 'Only available for private projects';

export const UNPUBLISHED_BID_SETUP_TOOLTIP =
  'Publish this bid setup before inviting bidders to this project.';

export interface SendInvitationEligibility {
  isArchivedProject: boolean;
  isPublicProject: boolean;
  bidSetupId: string | null | undefined;
  publishedContactUserId: string | null | undefined;
}

/**
 * Why the private invitation cannot be sent, as the sentence to show on the disabled button, or
 * undefined when it can. Everything the invitation needs — the password, the contact user and
 * their company — is read server-side off the published bid setup, so nothing typed into an
 * unsaved form can be invited against.
 */
export const getSendInvitationBlockedReason = ({
  isArchivedProject,
  isPublicProject,
  bidSetupId,
  publishedContactUserId,
}: SendInvitationEligibility): string | undefined => {
  if (isArchivedProject) return ARCHIVED_PROJECT_TOOLTIP;
  if (isPublicProject) return PUBLIC_PROJECT_TOOLTIP;
  if (!bidSetupId) return UNPUBLISHED_BID_SETUP_TOOLTIP;
  if (!publishedContactUserId) return MISSING_PROJECT_CONTACT_TOOLTIP;
  return undefined;
};

export interface ProjectBidInvitationEmailFields {
  projectName: string;
  bidContactCompanyName: string;
  privateJobKey: string;
}

/**
 * The body of the invitation email, for the preview the send dialog shows.
 *
 * This mirrors the plain-text template the backend actually sends,
 * backend/internal-api/emailing/templates/project-bid-invitation/project-bid-invitation-text.html —
 * edit the two together, or the preview stops telling the truth.
 */
export const getProjectBidInvitationEmailBody = ({
  projectName,
  bidContactCompanyName,
  privateJobKey,
}: ProjectBidInvitationEmailFields): string =>
  [
    `You have been invited to bid on a private project by ${bidContactCompanyName}.`,
    projectName,
    // Once bid packages are implemented, name the bid package here, on its own line directly below
    // the project name, in the same font size and styling as the project name above.
    'Complete bidding documents are available in electronic form at no cost from CENTERLINE BidConnect at https://centerlinebidconnect.com',
    'New users will need to set up a username & password. The site will then require verification of your email before you are allowed access.',
    'Once you have logged in, please click on "Enter Private Job Key" in the upper right corner of the screen.',
    `Private Job Key: ${privateJobKey}`,
    'From the project page in CENTERLINE BidConnect, you can get all the details on the project, download PDFs of the Bid Documents, receive Addenda as they are issued, register as a Prime Bidder, Submit RFIs and Substitution Requests, and Submit a Bid (when electronic bidding is allowed by the Project Owner).',
    'Please feel free to forward this information to your subcontractors and material suppliers to facilitate their access to the Bid Documents and ensure the immediate distribution of Addenda or other informational items to all interested parties.',
    'All technical questions about the project should be submitted via CENTERLINE BidConnect. Emailed questions will not be answered.',
  ].join('\n\n');
